import { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, Input, InputNumber, message, Typography, Popconfirm, Tooltip, Alert, Tabs, Statistic, Row, Col, Divider } from 'antd';
import { PlusOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, ApiOutlined, ThunderboltOutlined, ExperimentOutlined, CheckCircleOutlined, CloseCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { providerAPI } from '../services/api';

const { Text, Title, Paragraph } = Typography;

const VIDEO_MODES = [
  { value: 't2v', label: '文生视频 (T2V)' },
  { value: 'i2v', label: '图生视频 (I2V)' },
  { value: 'firstlast', label: '首尾帧 (Start/End Frames)' },
  { value: 'smart_multiframe', label: '智能多帧 (Smart MultiFrame)' },
  { value: 'full_reference', label: '全能参考 (Full Reference)' },
];

const TokeaseConfig = () => {
  const [providers, setProviders] = useState([]);
  const [modelConfigs, setModelConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('providers');
  const [providerModal, setProviderModal] = useState(false);
  const [modelModal, setModelModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [providerForm] = Form.useForm();
  const [modelForm] = Form.useForm();

  // 筛选词易供应商
  const tokeaseProviders = providers.filter(p => {
    const n = (p.name || '').toLowerCase();
    const d = (p.display_name || '').toLowerCase();
    return n.includes('huoshan') || d.includes('火山原版');
  });
  const tokeaseProviderIDs = tokeaseProviders.map(p => p.id);
  const tokeaseModels = modelConfigs.filter(m => tokeaseProviderIDs.includes(m.provider_id));

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        providerAPI.getProviders(),
        providerAPI.getModelConfigs(),
      ]);
      let providersData = [];
      if (Array.isArray(pRes)) providersData = pRes;
      else if (pRes?.data?.list) providersData = pRes.data.list;
      else if (pRes?.data) providersData = Array.isArray(pRes.data) ? pRes.data : [];
      setProviders(providersData);

      let modelsData = [];
      if (Array.isArray(mRes)) modelsData = mRes;
      else if (mRes?.data?.list) modelsData = mRes.data.list;
      else if (mRes?.data) modelsData = Array.isArray(mRes.data) ? mRes.data : [];
      setModelConfigs(modelsData);
    } catch (e) {
      message.error('加载数据失败: ' + (e.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ========== Provider CRUD ==========
  const openProviderModal = (record) => {
    setEditingProvider(record);
    if (record) {
      providerForm.setFieldsValue({
        name: record.name, display_name: record.display_name,
        api_base: record.api_base, api_key: record.api_key,
        description: record.description || '词易 Seedance 视频生成',
      });
    } else {
      providerForm.resetFields();
      providerForm.setFieldsValue({
        name: 'huoshan-seedance', display_name: 'Seedance(火山原版)',
        api_base: 'https://tokease.cn',
        description: '词易 Seedance 视频生成',
      });
    }
    setProviderModal(true);
  };

  const saveProvider = async () => {
    try {
      const values = await providerForm.validateFields();
      setSaving(true);
      const data = { ...values, category: 'material' };
      if (editingProvider) {
        await providerAPI.updateProvider(editingProvider.id, data);
        message.success('供应商已更新');
      } else {
        await providerAPI.createProvider(data);
        message.success('供应商已创建');
      }
      setProviderModal(false);
      loadData();
    } catch (e) { if (e.errorFields) return; message.error('保存失败: ' + (e.message || '未知错误')); }
    finally { setSaving(false); }
  };

  const deleteProvider = async (record) => {
    try { await providerAPI.deleteProvider(record.id); message.success('供应商已删除'); loadData(); }
    catch (e) { message.error('删除失败: ' + (e.message || '未知错误')); }
  };

  // ========== Test Connection ==========
  const testConnection = async (record) => {
    setTesting(prev => ({ ...prev, [record.id]: true }));
    try {
      await providerAPI.testProviderConnection({
        api_base: record.api_base,
        api_key: record.api_key,
      });
      message.success(`${record.display_name} 连接测试成功`);
    } catch (e) {
      message.error(`${record.display_name} 连接失败: ${e.message || '未知错误'}`);
    } finally {
      setTesting(prev => ({ ...prev, [record.id]: false }));
    }
  };

  // ========== Model Config CRUD ==========
  const openModelModal = (record) => {
    setEditingModel(record);
    if (record) {
      let vc = {};
      try { vc = JSON.parse(record.video_config || '{}'); } catch (e) {}
      const active = record.status === 'active' || record.is_active !== false;
      modelForm.setFieldsValue({
        model_name: record.model_name, display_name: record.display_name, provider_id: record.provider_id,
        type: record.type || 'video', api_path: record.api_path || '',
        is_active: active,
        supported_modes: vc.supported_modes || ['t2v'],
        billing_mode: vc.billing_mode || 'token_based',
        video_config_json: JSON.stringify(vc, null, 2),
      });
    } else {
      modelForm.resetFields();
      modelForm.setFieldsValue({ type: 'video', is_active: true,
        billing_mode: 'token_based', supported_modes: ['t2v', 'i2v', 'firstlast', 'smart_multiframe', 'full_reference'] });
    }
    setModelModal(true);
  };

  const saveModel = async () => {
    try {
      const values = await modelForm.validateFields();
      setSaving(true);
      // 视频配置 JSON：始终从表单 JSON 编辑器读取，保留完整配置
      let vc = {};
      try { vc = JSON.parse(values.video_config_json || '{}'); } catch (e) { message.error('video_config JSON 格式错误: ' + e.message); setSaving(false); return; }
      if (values.supported_modes?.length) vc.supported_modes = values.supported_modes;
      if (values.billing_mode) vc.billing_mode = values.billing_mode;
      const data = {
        display_name: values.display_name || values.model_name,
        type: values.type || 'video',
        billing_type: 'per_token',
        api_path: values.api_path || '',
        video_config: JSON.stringify(vc),
        status: values.is_active !== false ? 'active' : 'inactive',
      };
      if (editingModel) {
        await providerAPI.updateModelConfig(editingModel.id, data);
        message.success('模型配置已更新');
      } else {
        data.model_name = values.model_name;
        data.provider_id = values.provider_id;
        await providerAPI.createModelConfig(data);
        message.success('模型配置已创建');
      }
      setModelModal(false); loadData();
    } catch (e) { if (e.errorFields) return; message.error('保存失败: ' + (e.message || '未知错误')); }
    finally { setSaving(false); }
  };

  const deleteModel = async (record) => {
    try { await providerAPI.deleteModelConfig(record.id); message.success('模型配置已删除'); loadData(); }
    catch (e) { message.error('删除失败: ' + (e.message || '未知错误')); }
  };

  // ========== Columns ==========
  const providerColumns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '名称', dataIndex: 'name', width: 180 },
    { title: '显示名', dataIndex: 'display_name', width: 180 },
    { title: 'API 地址', dataIndex: 'api_base', ellipsis: true, render: v => <Text copyable style={{ fontSize: 12 }}>{v}</Text> },
    { title: '状态', dataIndex: 'status', width: 80,
      render: v => v === 'active' ? <Tag color="green" icon={<CheckCircleOutlined />}>启用</Tag> : <Tag color="red" icon={<CloseCircleOutlined />}>禁用</Tag>,
    },
    { title: '操作', width: 240, render: (_, r) => (
      <Space size="small">
        <Button size="small" icon={<ExperimentOutlined />} loading={testing[r.id]} onClick={() => testConnection(r)}>测试</Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => openProviderModal(r)}>编辑</Button>
        <Popconfirm title="确定删除？" onConfirm={() => deleteProvider(r)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  const modelColumns = [
    { title: 'ID', dataIndex: 'id', width: 45 },
    { title: '模型名', dataIndex: 'model_name', width: 160, render: v => <Text code>{v}</Text> },
    { title: '提供商', key: 'prov', width: 130,
      render: (_, r) => { const p = providers.find(x => x.id === r.provider_id); return <Tag>{p?.display_name || p?.name || r.provider_id}</Tag>; },
    },
    { title: '定价(分辨率/无参考/有参考)', key: 'price', width: 300,
      render: (_, r) => { let vc = {}; try { vc = JSON.parse(r.video_config || '{}'); } catch (e) {}; const rp = vc.resolution_pricing || {}; const rpw = vc.resolution_pricing_with_video || {}; const keys = Object.keys(rp); if (!keys.length) return <Text type="secondary">-</Text>; return <div style={{fontSize:11,lineHeight:'1.8'}}>{keys.map(k => <div key={k}>{k.toUpperCase()}<span style={{margin:'0 4px',color:'#ddd'}}>/</span>¥{rp[k]}{rpw[k]!=null?<><span style={{margin:'0 4px',color:'#ddd'}}>/</span><span style={{color:'#52c41a'}}>¥{rpw[k]}</span></>:null}</div>)}</div>; },
    },
    { title: '模式', key: 'modes', width: 200,
      render: (_, r) => { let vc = {}; try { vc = JSON.parse(r.video_config || '{}'); } catch (e) {}; const modes = vc.supported_modes || []; return modes.length > 0 ? modes.map(m => { const found = VIDEO_MODES.find(v => v.value === m); return <Tag key={m} color="blue" style={{ fontSize: 11 }}>{found?.label || m}</Tag>; }) : <Text type="secondary">-</Text>; },
    },
    { title: '计费', dataIndex: 'billing_type', width: 80, render: v => <Tag>{v || '-'}</Tag> },
    { title: '状态', dataIndex: 'is_active', width: 55,
      render: v => v !== false ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>,
    },
    { title: '操作', width: 130, render: (_, r) => (
      <Space size="small">
        <Button size="small" icon={<EditOutlined />} onClick={() => openModelModal(r)}>编辑</Button>
        <Popconfirm title="确定删除？" onConfirm={() => deleteModel(r)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <ThunderboltOutlined style={{ color: '#722ed1', marginRight: 8 }} />
            词易 Seedance 配置
          </Title>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
          {activeTab === 'providers' ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openProviderModal(null)}>添加供应商</Button>
          ) : (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModelModal(null)}>添加模型配置</Button>
          )}
        </Space>
      </div>

      {/* 接入指南 */}
      <Alert
        message="接入流程：① 添加词易上游供应商（API 地址 + Key） → ② 在「模型配置」添加具体模型（定价、生成模式） → ③ 用户端视频生成页面下拉选择词易 Seedance 模型使用"
        type="info" showIcon={false}
        style={{ marginBottom: 12, padding: '6px 14px' }}
      />

      {/* 标签页 */}
      <Card styles={{ body: { paddingTop: 4 } }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'providers',
            label: <span><ApiOutlined /> 供应商管理</span>,
            children: (
              <Table dataSource={tokeaseProviders} columns={providerColumns} rowKey="id" loading={loading} size="middle" pagination={false}
                locale={{ emptyText: '暂无供应商，请先添加词易供应商' }} />
            ),
          },
          {
            key: 'models',
            label: <span><ThunderboltOutlined /> 模型配置</span>,
            children: (
              <Table dataSource={tokeaseModels} columns={modelColumns} rowKey="id" loading={loading} size="middle" pagination={false}
                locale={{ emptyText: '暂无模型配置，请先添加模型' }} />
            ),
          },
        ]} />
      </Card>

      {/* Provider Modal */}
      <Modal title={editingProvider ? '编辑供应商' : '添加词易供应商'} open={providerModal} onCancel={() => setProviderModal(false)} onOk={saveProvider} confirmLoading={saving} width={560} destroyOnHidden>
        <Form form={providerForm} layout="vertical">
          <Form.Item name="name" label="提供商标识" rules={[{ required: true }]}><Input placeholder="如 huoshan-seedance" /></Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true }]}><Input placeholder="如 Seedance(火山原版)" /></Form.Item>
          <Form.Item name="api_base" label="API 基础地址" rules={[{ required: true }]}><Input placeholder="https://tokease.cn" prefix={<LinkOutlined />} /></Form.Item>
          <Form.Item name="api_key" label="API Key" rules={[{ required: true }]}><Input.Password placeholder="词易 API Key" /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Model Modal */}
      <Modal title={editingModel ? '编辑模型配置' : '添加词易模型'} open={modelModal} onCancel={() => setModelModal(false)} onOk={saveModel} confirmLoading={saving} width={640} destroyOnHidden>
        <Form form={modelForm} layout="vertical">
          <Form.Item name="model_name" label="模型名称" rules={[{ required: true }]}><Input placeholder="如 seedance-2.0" /></Form.Item>
          <Form.Item name="display_name" label="显示名称"><Input placeholder="如 Seedance 2.0" /></Form.Item>
          <Form.Item name="provider_id" label="供应商 ID" rules={[{ required: true }]}>
            <Select placeholder="选择供应商" options={tokeaseProviders.map(p => ({ value: p.id, label: `${p.display_name || p.name} (ID:${p.id})` }))} />
          </Form.Item>
          <Form.Item name="api_path" label="API 路径"><Input placeholder="/v1/video/generations" /></Form.Item>
          <Form.Item name="type" label="模型类型"><Select options={[{ value: 'video', label: '视频生成 (video)' }]} /></Form.Item>
          <Form.Item name="is_active" label="启用"><Select options={[{ value: true, label: '启用' }, { value: false, label: '禁用' }]} /></Form.Item>
          <Divider>支持的生成模式</Divider>
          <Form.Item name="supported_modes"><Select mode="multiple" placeholder="选择支持的生成模式" options={VIDEO_MODES} /></Form.Item>
          <Divider>完整 JSON 配置</Divider>
          <Form.Item name="video_config_json" label="video_config JSON" extra="直接编辑 JSON 配置，包含分辨率、时长、比例、参考图上限等所有参数。留空则使用下方分字段配置。" rules={[{ validator: (_, v) => { if (!v) return Promise.resolve(); try { JSON.parse(v); return Promise.resolve(); } catch(e) { return Promise.reject('JSON 格式错误'); } } }]}>
            <Input.TextArea rows={10} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder='{"resolutions":[...],"durationRange":[4,24],...}' />
          </Form.Item>
          <Divider>分字段配置（JSON 留空时使用）</Divider>
          <Form.Item name="billing_mode" label="计费模式">
            <Select options={[
              { value: 'token_based', label: 'Token 公式计费 (Seedance)' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TokeaseConfig;
